import React, { useEffect, useMemo, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { Router, Switch, Route } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { withResizeDetector } from 'react-resize-detector';
import { ToastContainer } from 'react-toastify';
import styled from '@emotion/styled';
import { ThemeProvider } from '@emotion/react';
import createTheme from '@styles/theme/index';
import Logo from '@assets/logo.png';
import GlobalStyle, { SuspenseLoader } from '@styles/globalStyles';
import CalendarStyles from '@styles/calendarStyles';
import routes from '@routes';
import { history } from '@utils/history';
import Loader from '@components/Loader';

import Calendar from '@containers/Calendar';
import RegulationsModal from '@containers/Summary/components/RegulationsModal';

import 'react-toastify/dist/ReactToastify.css';
import { THEMES } from '@styles/theme/variants';

const Payment = lazy(() => import('@containers/Payment'));
const PaymentConfirmation = lazy(() =>
  import('@containers/PaymentConfirmation'),
);
const PaymentLoading = lazy(() => import('@containers/PaymentLoading'));
const Summary = lazy(() => import('@containers/Summary'));
const Demo = lazy(() => import('@containers/Demo'));

const PageWrapper = styled.div``;

const App = ({
  widgetStore: { initialized, isLoading, fitIframeToContent, customStyle },
  height,
}) => {
  useEffect(() => {
    fitIframeToContent(height);
  }, [height]);

  const theme = useMemo(() => createTheme(THEMES.DEFAULT, customStyle), [
    customStyle,
  ]);

  const isDemoPage = useMemo(() => history.location.pathname === routes.demo, [
    history.location.pathname,
  ]);

  const LoadingFallback = useMemo(
    () => (
      <SuspenseLoader>
        <svg
          width="100"
          height="100"
          viewBox="0 0 512 513"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M255.5 0L477.635 128.25V384.75L255.5 513L33.3645 384.75V128.25L255.5 0Z"
            fill="#0077B6"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            // eslint-disable-next-line max-len
            d="M256.264 407.337C249.54 407.337 244.089 401.886 244.089 395.162V371.162C221.125 368.846 200.138 359.833 183.111 346.106L166.284 362.934C161.529 367.689 153.82 367.689 149.065 362.934C144.31 358.179 144.31 350.47 149.065 345.715L165.851 328.929C152.006 311.921 142.884 290.915 140.485 267.91H116.837C110.113 267.91 104.662 262.459 104.662 255.734C104.662 249.01 110.113 243.559 116.837 243.559H140.485C142.884 220.554 152.006 199.547 165.851 182.54L149.065 165.753C144.31 160.999 144.31 153.29 149.065 148.535C153.82 143.78 161.529 143.78 166.284 148.535L183.111 165.362C200.139 151.636 221.125 142.623 244.089 140.306V116.307C244.089 109.582 249.54 104.131 256.264 104.131C262.988 104.131 268.44 109.582 268.44 116.307V140.388C291.292 142.849 312.156 151.946 329.067 165.713L346.245 148.535C351 143.78 358.709 143.78 363.464 148.535C368.219 153.29 368.219 160.999 363.464 165.753L346.245 182.973C359.893 199.903 368.882 220.748 371.26 243.559H395.693C402.417 243.559 407.868 249.01 407.868 255.734C407.868 262.459 402.417 267.91 395.693 267.91H371.26C368.882 290.72 359.893 311.565 346.245 328.496L363.464 345.715C368.219 350.47 368.219 358.179 363.464 362.934C358.709 367.689 351 367.689 346.246 362.934L329.067 345.755C312.156 359.522 291.292 368.619 268.44 371.081V395.162C268.44 401.886 262.988 407.337 256.264 407.337ZM167.062 243.559C169.2 227.814 175.427 213.368 184.639 201.327L226.871 243.559H167.062ZM268.44 344.491V285.128L310.28 326.968C298.338 336.103 284.032 342.303 268.44 344.491ZM285.659 267.91L327.445 309.696C336.47 297.744 342.571 283.461 344.683 267.91H285.659ZM244.089 166.872V226.34L201.911 184.162C213.963 175.061 228.386 168.934 244.089 166.872ZM310.28 184.5C298.338 175.366 284.032 169.166 268.44 166.978V226.341L310.28 184.5ZM226.871 267.91H167.062C169.2 283.655 175.427 298.1 184.638 310.142L226.871 267.91ZM201.911 327.307L244.089 285.129V344.597C228.386 342.535 213.962 336.408 201.911 327.307ZM285.658 243.559L327.445 201.772C336.47 213.724 342.571 228.008 344.683 243.559H285.658ZM255.99 270.723C264.116 270.723 270.703 264.135 270.703 256.01C270.703 247.884 264.116 241.296 255.99 241.296C247.864 241.296 241.277 247.884 241.277 256.01C241.277 264.135 247.864 270.723 255.99 270.723Z"
            fill="white"
          />
        </svg>
      </SuspenseLoader>
    ),
    [],
  );

  return (
    <Suspense fallback={LoadingFallback}>
      <ThemeProvider theme={theme}>
        <PageWrapper>
          <GlobalStyle />
          <CalendarStyles />
          <CSSTransition
            in={!isDemoPage && (!initialized || isLoading)}
            timeout={300}
            classNames="loader"
            unmountOnExit
          >
            <Loader>
              <img src={Logo} alt="" />
            </Loader>
          </CSSTransition>

          <Router history={history}>
            <Switch>
              <Route exact path={routes.demo} component={Demo} />
              <Route exact path={routes.root} component={Calendar} />
              <Route exact path={routes.summary} component={Summary} />
              <Route exact path={routes.payment} component={Payment} />
              <Route
                exact
                path={routes.paymentpaymentLoading}
                component={PaymentLoading}
              />
              <Route
                exact
                path={routes.paymentConfirmation}
                component={PaymentConfirmation}
              />
            </Switch>
          </Router>
          <ToastContainer />
          <RegulationsModal />
        </PageWrapper>
      </ThemeProvider>
    </Suspense>
  );
};

App.defaultProps = {
  height: 300,
};

App.propTypes = {
  widgetStore: PropTypes.shape({
    initialized: PropTypes.bool,
    isLoading: PropTypes.bool,
    fitIframeToContent: PropTypes.func,
    customStyle: PropTypes.object,
  }).isRequired,
  bookingStore: PropTypes.shape({
    regulationsModalIsVisible: PropTypes.bool,
  }).isRequired,
  height: PropTypes.number,
};

export default withResizeDetector(
  inject('bookingStore', 'widgetStore')(observer(App)),
);
